import { unstable_cache as cache } from 'next/cache'
import { groupBy, omit, prop } from 'ramda'

import { CONTENT_LANG, env } from '@/constants/configs'
import { locales } from '@/libs/intl'
import { transformAndGroup } from '@/libs/transform'

import { createHeaders } from './helpers'

export const getTranslations = cache(
  async () => {
    const headers = createHeaders()
    const response = await fetch(`${env.API_GATEWAY}/api/translation/`, { method: 'post', credentials: 'include', headers })
    const data = await response.json()

    return transformAndGroup(locales, data.data.translations)
  },
  ['translations'],
  {
    revalidate: 60,
    tags: ['translations']
  }
)

export const getCommon = (locale: string = 'en') =>
  cache(
    async () => {
      const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
      const response = await fetch(`${env.API_GATEWAY}/api/home/`, {
        method: 'post',
        credentials: 'include',
        headers
      })
      const { data } = await response.json()

      return omit(['csrf_token'], data)
    },
    ['common', locale],
    {
      revalidate: 60,
      tags: ['common']
    }
  )()

export const getBranches = (locale: string = 'en') =>
  cache(
    async () => {
      const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
      const response = await fetch(`${env.API_GATEWAY}/api/location/`, {
        method: 'post',
        credentials: 'include',
        headers
      })
      const { data } = await response.json()

      const sorted = groupBy(prop('city_code'), data) as Record<string, any[]>
      const result = Object.keys(sorted).map((key) => {
        const city = sorted[key][0]

        return {
          id: city['city_id'],
          code: city['city_code'],
          name: city['city'],
          branches: sorted[key]
        }
      }) as IBranch[]

      return result
    },
    ['branches', locale],
    {
      revalidate: 60,
      tags: ['branches']
    }
  )()

export const getBlogs = (locale: string = 'en') =>
  cache(
    async () => {
      const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
      const response = await fetch(`${env.API_GATEWAY}/api/tos/blog/`, {
        method: 'post',
        credentials: 'include',
        headers
      })
      const data = await response.json()

      return data.data
    },
    ['blogs'],
    {
      revalidate: 60,
      tags: ['blogs']
    }
  )()

export const getPromotions = async (locale: string = 'en') => {
  // cache(
  //   async () => {
  const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
  const formdata = new FormData()
  formdata.append('show', '1')
  formdata.append('isPublic', '1')

  const response = await fetch(`${env.API_GATEWAY}/api/promotions/`, {
    method: 'post',
    credentials: 'include',
    headers,
    body: formdata,
    redirect: 'follow',
    cache: 'no-store',
    next: { revalidate: 0 }
  })

  const { data } = await response.json()

  return data
}

export const getPromotionDetail = async (locale: string = 'en', slug: string) => {
  const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
  const formdata = new FormData()
  formdata.append('slug', slug)
  formdata.append('isPublic', '1')

  const response = await fetch(`${env.API_GATEWAY}/api/promotion/`, {
    method: 'post',
    headers,
    body: formdata,
    redirect: 'follow',
    credentials: 'include'
  })

  const { data } = await response.json()

  return data
}

export const getPaymentLink = async (slug: string) => {
  const SMS_URL = env.SMS_URL || ''

  try {
    const response = await fetch(`${SMS_URL}/api/payment-links/${slug}?bookinfo=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Api-Key': env.SMS_API_KEY || ''
      },
      credentials: 'include'
    })

    if (!response.ok) {
      // Parse Retry-After header for rate limit responses
      let retryAfter: number | undefined
      if (response.status === 429) {
        const retryAfterHeader = response.headers.get('Retry-After')
        if (retryAfterHeader) {
          retryAfter = parseInt(retryAfterHeader, 10)
        }
      }

      return {
        success: false,
        error: response.statusText,
        status: response.status,
        data: null,
        retryAfter
      }
    }

    const data = await response.json()
    return {
      success: true,
      error: null,
      status: response.status,
      data: data
    }
  } catch (error) {
    console.error('Payment link fetch error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0,
      data: null
    }
  }
}

export const getPaymentLinkClient = async (slug: string) => {
  try {
    const response = await fetch(`/api/payment-links/${slug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    })

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Payment link fetch error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0,
      data: null
    }
  }
}

export const getGiftProducts = async (locale: string = 'en') => {
  try {
    const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])

    const formdata = new FormData()
    formdata.append('certificate_page', 'true')

    const response = await fetch(`${env.API_GATEWAY}/api/product/eGift/`, {
      method: 'POST',
      headers: headers,
      body: formdata,
      cache: 'no-store',
      next: { revalidate: 0 },
      credentials: 'include'
    })

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Gift Products API Error:', error)
    throw error
  }
}

export const getOrderDetail = (
  locale: string = 'en',
  reference: string,
  session: string,
  refType: number = 2
) =>
  cache(
    async () => {
      const headers = createHeaders([{ name: CONTENT_LANG, value: locale }])
      const formdata = new FormData()
      formdata.append('reference', reference)
      formdata.append('session', session)
      formdata.append('ref_type', refType.toString())

      const response = await fetch(`${env.API_GATEWAY}/api/order/`, {
        method: 'post',
        headers,
        body: formdata,
        credentials: 'include'
      })

      const result = await response.json()
      if (!result || !result.data) {
        throw new Error(result.err_code || 'Order data not found')
      }

      return result
    },
    ['orderDetail', locale, reference, refType.toString()],
    {
      revalidate: 60,
      tags: ['orderDetail']
    }
  )()

export const getCartCount = async () => {
  try {
    const { cookies } = await import('next/headers')
    const cookieStore = await cookies()
    const cookieHeader = cookieStore
      .getAll()
      .map((c) => `${c.name}=${c.value}`)
      .join('; ')

    const response = await fetch(`${env.API_GATEWAY}/api/cart/`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Secretkey: env.API_SECRET_KEY,
        Cookie: cookieHeader
      },
      cache: 'no-store'
    })

    if (!response.ok) {
      return 0
    }

    const result = await response.json()
    const count = Number(result?.data?.count ?? 0)
    return count
  } catch (error) {
    console.error('Cart count fetch error:', error)
    return 0
  }
}
